// deviceComparison.js
// Pure, side-effect-free logic for the "AssetPro <-> IoT-Ops Device Comparison"
// report. Compares which devices AssetPro thinks are installed on each asset
// against what the IoT-Ops integration registry reports, and categorises the
// differences. Compare-and-display only: nothing here writes or syncs.
//
// DATA-RELIABILITY CONTRACT (see AssetPro CLAUDE.md):
//   - Matching is EXACT on normalized identifiers. No fuzzy / partial / name
//     matching. Unmatched stays unmatched.
//   - Nothing is fabricated or defaulted. A missing id is missing, not zero.
//   - Uncertain cases (fallback-sourced id, malformed MAC, duplicate id) are
//     flagged as "needs review" rather than silently matched.
//
// Identifier normalization (must be applied identically to BOTH sides so an
// exact compare is meaningful):
//   - GPS  : AssetPro tagDeviceId  <-> Ops imei      -> trim only (exact)
//   - BLE  : AssetPro bleTagId     <-> Ops mac       -> strip non-hex, uppercase,
//                                                       colon-group (mirrors the
//                                                       asset_tagging Flutter app's
//                                                       CapturedSensor.normalizeId)
//   - FUEL : AssetPro fuelSensorId <-> Ops serialNo  -> trim only (exact)

// ---- Device kinds --------------------------------------------------------

export const DEVICE_KIND = {
  GPS: 'GPS',
  BLE: 'BLE',
  FUEL: 'FUEL',
  SIM: 'SIM',
  OTHER: 'OTHER'
};

export const DEVICE_KIND_LABEL = {
  GPS: 'GPS / IoT',
  BLE: 'BLE / Movement',
  FUEL: 'Fuel Sensor',
  SIM: 'SIM',
  OTHER: 'Other'
};

// Map an Ops hardwareRole to our normalized kind (display + category only —
// matching is by identifier, not by kind).
const roleToKind = (role) => {
  const r = String(role || '').toUpperCase();
  if (r.includes('FUEL')) return DEVICE_KIND.FUEL;
  if (r.includes('GPS') || r.includes('IOT')) return DEVICE_KIND.GPS;
  if (r.includes('MOVEMENT') || r.includes('BLE') || r.includes('BEACON') || r.includes('EYE')) return DEVICE_KIND.BLE;
  if (r.includes('SIM')) return DEVICE_KIND.SIM;
  return DEVICE_KIND.OTHER;
};

// ---- Row categories ------------------------------------------------------

export const CATEGORY = {
  MATCH: 'in_both_matching',
  DEVICE_MISMATCH: 'device_mismatch',
  ONLY_ASSETPRO: 'only_assetpro',
  ONLY_OPS: 'only_ops',
  NEEDS_REVIEW: 'needs_review'
};

export const CATEGORY_LABEL = {
  [CATEGORY.MATCH]: 'In both & matching',
  [CATEGORY.DEVICE_MISMATCH]: 'Device mismatch',
  [CATEGORY.ONLY_ASSETPRO]: 'Only in AssetPro',
  [CATEGORY.ONLY_OPS]: 'Only in IoT-Ops',
  [CATEGORY.NEEDS_REVIEW]: 'Needs review'
};

export const CATEGORY_COLOR = {
  [CATEGORY.MATCH]: 'green',
  [CATEGORY.DEVICE_MISMATCH]: 'orange',
  [CATEGORY.ONLY_ASSETPRO]: 'blue',
  [CATEGORY.ONLY_OPS]: 'purple',
  [CATEGORY.NEEDS_REVIEW]: 'red'
};

// ---- Normalization -------------------------------------------------------

const trimExact = (v) => String(v === null || v === undefined ? '' : v).trim();

// Canonical BLE MAC: hex-only, uppercase. Colon-grouped form is derived for
// display. We compare on the hex-only form so colon/case/separator differences
// never cause a false mismatch, while a genuinely different address still does.
const macHex = (v) => trimExact(v).replace(/[^0-9a-fA-F]/g, '').toUpperCase();

export const isValidMac = (v) => macHex(v).length === 12;

export const formatMac = (v) => {
  const hex = macHex(v);
  if (hex.length !== 12) return trimExact(v).toUpperCase(); // non-standard: show as-is (uppercased)
  return hex.match(/.{2}/g).join(':');
};

// Comparison key for a (kind, rawId) pair. Returns '' when the id is empty so
// callers can treat it as "no identifier" rather than matching blanks together.
export const canonicalKey = (kind, rawId) => {
  const raw = trimExact(rawId);
  if (!raw) return '';
  if (kind === DEVICE_KIND.BLE) return macHex(raw);
  return raw; // GPS imei / fuel serialNo: exact trim
};

// Human-facing normalized id (colon-grouped MAC; trimmed otherwise).
export const displayId = (kind, rawId) => {
  const raw = trimExact(rawId);
  if (!raw) return '';
  if (kind === DEVICE_KIND.BLE) return formatMac(raw);
  return raw;
};

// ---- AssetPro side: classify + group assets with their devices -----------

const assetModelOf = (a) => (a && a.assetModel && typeof a.assetModel === 'object' ? a.assetModel : {});

// Is this asset RECORD itself a device (GPS/BLE/fuel-sensor child asset)?
// Classified STRICTLY by the asset MODEL device flags — /asset/asset-all populates
// the `assetModel` object with these booleans (equipmentSubType is only an id, so
// no name matching). We do NOT classify by the presence of a
// tagDeviceId/bleTagId/fuelSensorId field, because a normal host asset can carry
// those directly (a directly-tagged master). The exact flags are:
//   GPS  : assetModel.isTagDevice
//   Fuel : assetModel.isFuelSensorDevice
//   BLE  : assetModel.isBleTag   (note: NOT "isBleDevice")
export const deviceKindOf = (a) => {
  const m = assetModelOf(a);
  if (m.isFuelSensorDevice === true) return DEVICE_KIND.FUEL;
  if (m.isTagDevice === true) return DEVICE_KIND.GPS;
  if (m.isBleTag === true) return DEVICE_KIND.BLE;
  return null;
};

export const isDeviceAsset = (a) => deviceKindOf(a) !== null;

const idOfRef = (ref) => {
  if (!ref) return null;
  if (typeof ref === 'string') return ref;
  if (typeof ref === 'object') return ref._id || ref.id || null;
  return null;
};

const parentIdOf = (a) =>
  idOfRef(a.parent) || idOfRef(a.parentId) || idOfRef(a.parentAsset) || idOfRef(a.host) || null;

const ownerCodeOf = (a) => {
  if (!a) return null;
  if (a.ownerCode) return a.ownerCode;
  if (a.owner_code) return a.owner_code;
  const owner = a.owner || a.assetOwner || a.custodian;
  if (owner && typeof owner === 'object') return owner.code || owner.employeeCode || null;
  return null;
};

const assetCodeOf = (a) => (a && (a.assetCode || a.code)) || null;

// Reserved AssetPro location that sold assets are moved to.
export const SOLD_LOCATION = 'SOLD';

// /asset/asset-all sends the asset's location as an ID only (a string, or an
// object with just `_id`); the NAME lives in the /location list. So we capture
// the id here and resolve the name separately (see compareSides options).
const locationIdOf = (a) => {
  if (!a) return null;
  const id = idOfRef(a.location);
  return id ? String(id) : null;
};

// Only return a name when the location object is actually populated with one
// (the single-asset endpoint does this); for asset-all this is null.
const locationNameOf = (a) => {
  const loc = a && a.location;
  if (loc && typeof loc === 'object' && (loc.name || loc.code)) return loc.name || loc.code;
  return typeof (a && a.locationName) === 'string' ? a.locationName : null;
};

export const isSoldLocation = (name) =>
  String(name === null || name === undefined ? '' : name).trim().toUpperCase() === SOLD_LOCATION;

// Extract the comparable identifier from a device asset record for its kind.
// For GPS and FUEL, in-stock devices carry the id in altName instead of the
// dedicated field, so we fall back to altName and FLAG it (fallback:true) — the
// caller surfaces the flag and downgrades unmatched fallbacks to "needs review"
// rather than trusting an altName silently. BLE has no altName fallback.
const extractDeviceId = (device, kind) => {
  if (kind === DEVICE_KIND.GPS) {
    if (device.tagDeviceId) return { value: trimExact(device.tagDeviceId), fallback: false };
    if (device.altName) return { value: trimExact(device.altName), fallback: true };
    return null;
  }
  if (kind === DEVICE_KIND.FUEL) {
    if (device.fuelSensorId) return { value: trimExact(device.fuelSensorId), fallback: false };
    if (device.altName) return { value: trimExact(device.altName), fallback: true };
    return null;
  }
  if (kind === DEVICE_KIND.BLE) {
    if (device.bleTagId) return { value: trimExact(device.bleTagId), fallback: false };
    return null;
  }
  return null;
};

/**
 * Build the AssetPro side: a Map of assetCode -> host descriptor with the list
 * of devices AssetPro believes are installed on it.
 *
 * Handles both response shapes this API can return:
 *   - flat device child records linked to their host via parentId, and
 *   - devices nested inside a host's `dependents` array (fuel sensor nested
 *     under its GPS device, which is nested under the host asset).
 * A device that is a child of another device (fuel sensor under GPS) is walked
 * up the parent chain to the NEAREST NON-DEVICE asset (the real host).
 *
 * @param {Array<object>} assets - flat list from /asset/asset-all
 * @returns {{ byCode: Map, orphans: Array, stats: object }}
 */
export function buildAssetProSide(assets) {
  const list = Array.isArray(assets) ? assets : [];

  const byId = new Map();
  list.forEach((a) => {
    const id = a && (a._id || a.id);
    if (id) byId.set(String(id), a);
  });

  const byCode = new Map();
  const orphans = []; // device records whose host could not be resolved
  const seenDeviceIds = new Set(); // dedupe devices seen via both flat + nested
  const stats = { assets: list.length, hosts: 0, deviceRecords: 0, orphanDevices: 0 };

  const hostRecord = (host) => {
    const code = assetCodeOf(host);
    if (!code) return null;
    if (!byCode.has(code)) {
      byCode.set(code, {
        assetCode: code,
        assetId: host._id || host.id || null,
        altName: host.altName || null,
        ownerCode: ownerCodeOf(host),
        locationId: locationIdOf(host),
        locationName: locationNameOf(host),
        devices: [],
        raw: host
      });
      stats.hosts += 1;
    }
    return byCode.get(code);
  };

  // Add a device (kind + id) to a host, de-duplicated by (kind, canonical id)
  // so the same physical device seen through multiple shapes isn't double counted.
  const addDevice = (rec, kind, extracted, deviceAsset, source) => {
    if (!rec || !kind || !extracted) return;
    const key = canonicalKey(kind, extracted.value);
    if (!key) return; // no usable id
    const dupWithin = rec.devices.some((d) => d.kind === kind && canonicalKey(kind, d.rawId) === key);
    rec.devices.push({
      kind,
      rawId: extracted.value,
      fallback: extracted.fallback === true,
      duplicate: dupWithin, // same id already present on this host -> ambiguous
      deviceCode: (deviceAsset && (deviceAsset.assetCode || deviceAsset.altName)) || null,
      source, // 'child' | 'nested' | 'inline' (traceability only)
      raw: deviceAsset || null
    });
  };

  const attachDeviceRecord = (deviceAsset, host, source) => {
    const kind = deviceKindOf(deviceAsset);
    if (!kind) return;
    const did = deviceAsset._id || deviceAsset.id;
    if (did) {
      if (seenDeviceIds.has(String(did))) return;
      seenDeviceIds.add(String(did));
    }
    stats.deviceRecords += 1;
    const rec = hostRecord(host);
    if (!rec) {
      stats.orphanDevices += 1;
      orphans.push({ kind, deviceAsset });
      return;
    }
    addDevice(rec, kind, extractDeviceId(deviceAsset, kind), deviceAsset, source);
  };

  // A non-device host may carry a device id directly on itself (directly-tagged
  // master). Capture those as inline devices of that host.
  const attachInlineDevices = (host) => {
    const rec = hostRecord(host);
    if (!rec) return;
    if (host.tagDeviceId) {
      addDevice(rec, DEVICE_KIND.GPS, { value: trimExact(host.tagDeviceId), fallback: false }, host, 'inline');
    }
    if (host.bleTagId) {
      addDevice(rec, DEVICE_KIND.BLE, { value: trimExact(host.bleTagId), fallback: false }, host, 'inline');
    }
    if (host.fuelSensorId) {
      addDevice(rec, DEVICE_KIND.FUEL, { value: trimExact(host.fuelSensorId), fallback: false }, host, 'inline');
    }
  };

  // Climb parentId links to the nearest NON-DEVICE host. Guards missing links
  // and cycles. Returns null if no non-device host is reachable.
  const resolveHost = (a) => {
    let current = a;
    const seen = new Set();
    for (let i = 0; i < 8; i += 1) {
      if (!isDeviceAsset(current)) return current;
      const pid = parentIdOf(current);
      if (!pid) return null;
      const key = String(pid);
      if (seen.has(key)) return null;
      seen.add(key);
      const parent = byId.get(key);
      if (!parent) return null; // parent not in fetched set
      current = parent;
    }
    return null;
  };

  // Recursively attach every device found in a host's dependents subtree.
  const walkDependents = (node, host) => {
    const deps = node && node.dependents;
    if (!Array.isArray(deps)) return;
    deps.forEach((dep) => {
      if (dep && typeof dep === 'object') {
        if (isDeviceAsset(dep)) attachDeviceRecord(dep, host, 'nested');
        walkDependents(dep, host); // fuel sensor nested under GPS
      }
    });
  };

  // Pass 1: register every non-device host, its inline devices, and its nested
  // device dependents.
  list.forEach((a) => {
    if (!isDeviceAsset(a)) {
      attachInlineDevices(a);
      walkDependents(a, a);
    }
  });

  // Pass 2: flat device records — resolve each to its non-device host.
  list.forEach((a) => {
    if (isDeviceAsset(a)) {
      const host = resolveHost(a);
      if (host && !isDeviceAsset(host)) attachDeviceRecord(a, host, 'child');
      else attachDeviceRecord(a, null, 'child'); // -> orphan bucket
    }
  });

  return { byCode, orphans, stats };
}

// ---- IoT-Ops side: group registry rows by assetCode ----------------------

/**
 * Build the IoT-Ops side: Map of assetCode -> { assetCode, devices, raw }.
 *
 * KEY MODELLING RULE: one `devicesByRole[].items[]` entry = ONE physical device.
 * Its `hardwareRole` (GPS_DEVICE / FUEL_SENSOR / MOVEMENT_SENSOR / SIM) is the
 * device kind, and its `imei` / `mac` / `serialNo` / `simNo` are DIFFERENT
 * identifiers OF THAT SAME device (any subset may be null). We must NOT split one
 * item into several devices by id-field — a GPS device's `serialNo` is the GPS's
 * own serial (not a fuel sensor), and a fuel sensor's `mac` is the fuel sensor's
 * BLE address (not a separate BLE tag).
 *
 * @param {Array<object>} rows - rows from GET /integration/devices
 */
export function buildOpsSide(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const byCode = new Map();
  const stats = { rows: list.length, hosts: 0, deviceCount: 0, rowsWithoutCode: 0, clients: [] };

  // The integration token is hard-scoped to ONE ops client (organisation), so a
  // well-formed response should reference exactly one client. We tally the
  // distinct clients seen so the UI can (a) show which org the token belongs to
  // and (b) warn if a response ever spans more than one client.
  const clientTally = new Map();

  list.forEach((row) => {
    const client = row && row.client;
    if (client && (client.id || client.name)) {
      const key = String(client.id || client.name);
      if (!clientTally.has(key)) clientTally.set(key, { id: client.id || null, name: client.name || null, count: 0 });
      clientTally.get(key).count += 1;
    }

    const code = row && row.asset && (row.asset.assetCode || row.asset.code);
    if (!code) {
      stats.rowsWithoutCode += 1;
      return; // cannot compare a device row with no assetCode
    }
    if (!byCode.has(code)) {
      byCode.set(code, { assetCode: code, devices: [], raw: row });
      stats.hosts += 1;
    }
    const rec = byCode.get(code);

    const roles = Array.isArray(row.devicesByRole) ? row.devicesByRole : [];
    roles.forEach((roleGroup) => {
      const items = Array.isArray(roleGroup.items) ? roleGroup.items : [];
      items.forEach((item) => {
        const role = item.hardwareRole || item.itemType || roleGroup.role || roleGroup.hardwareRole || null;
        const imei = trimExact(item.imei);
        const mac = trimExact(item.mac);
        const serial = trimExact(item.serialNo);
        const sim = trimExact(item.simNo);
        // A device with role missing falls back to id-shape inference so this
        // still works if the API ever omits hardwareRole.
        let kind = roleToKind(role);
        if (kind === DEVICE_KIND.OTHER) {
          if (imei) kind = DEVICE_KIND.GPS;
          else if (mac) kind = DEVICE_KIND.BLE;
          else if (serial) kind = DEVICE_KIND.FUEL;
          else if (sim) kind = DEVICE_KIND.SIM;
        }
        rec.devices.push({
          role: role || null,
          kind,
          imei: imei || null,
          mac: mac || null,
          macHex: mac ? macHex(mac) : null,
          serial: serial || null,
          sim: sim || null,
          model: item.model || null,
          inventoryItemId: item.inventoryItemId || null,
          status: item.status || null,
          raw: item
        });
        stats.deviceCount += 1;
      });
    });
  });

  stats.clients = Array.from(clientTally.values());
  return { byCode, stats };
}

// ---- Compare -------------------------------------------------------------

// Normalized comparison token for a single identifier VALUE. Matching is by
// value across ANY field (not locked to the device's "kind"), because the two
// systems don't always store an id in the same-named field — e.g. a MAC can sit
// in AssetPro's tagDeviceId (a GPS-typed record) while IoT-Ops has it as a
// MOVEMENT_SENSOR `mac`. A MAC-shaped value (exactly 12 hex chars after stripping
// separators) is compared colon/'-'-insensitively; everything else is compared
// exactly (trimmed, case-insensitive). The namespace prefix keeps a MAC-shaped
// value from colliding with a longer id and vice versa.
const normToken = (value) => {
  const t = trimExact(value);
  if (!t) return null;
  const hex = t.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  if (hex.length === 12) return `MAC:${hex}`; // MAC or 12-hex id, separator-insensitive
  return `ID:${t.toUpperCase()}`;
};

// Every comparison token an Ops device carries (imei / mac / serial). simNo is
// excluded — AssetPro never holds a SIM number, so it must not create a match.
const opsMatchTokens = (ops) => {
  const set = new Set();
  [ops.imei, ops.mac, ops.serial].forEach((v) => {
    const t = normToken(v);
    if (t) set.add(t);
  });
  return set;
};

// An Ops device is "comparable" only if it carries an identifier AssetPro could
// also hold (imei / mac / serial). A SIM (simNo only) is real but not something
// AssetPro tracks as a device, so it is shown as informational and never flips an
// asset to "mismatch".
const opsComparable = (ops) => Boolean(ops.imei || ops.mac || ops.serial);

// Human-facing rendering of an identifier value: MAC-shaped values are shown
// colon-grouped; everything else trimmed as-is.
const displayValue = (v) => {
  const t = trimExact(v);
  if (!t) return '';
  return t.replace(/[^0-9a-fA-F]/g, '').length === 12 ? formatMac(t) : t;
};

// Best display id for an Ops device, by its kind.
const opsDisplayId = (ops) => {
  if (ops.kind === DEVICE_KIND.GPS && ops.imei) return displayValue(ops.imei);
  if (ops.kind === DEVICE_KIND.BLE && ops.mac) return displayValue(ops.mac);
  if (ops.kind === DEVICE_KIND.FUEL && ops.serial) return displayValue(ops.serial);
  if (ops.kind === DEVICE_KIND.SIM && ops.sim) return trimExact(ops.sim);
  return displayValue(ops.imei) || displayValue(ops.mac) || displayValue(ops.serial) || trimExact(ops.sim) || '';
};

// The Ops device identifiers other than the one that matched, for a trace note.
const opsOtherIdsExcept = (ops, matchedToken) => {
  const parts = [];
  if (ops.imei && normToken(ops.imei) !== matchedToken) parts.push(`imei ${displayValue(ops.imei)}`);
  if (ops.mac && normToken(ops.mac) !== matchedToken) parts.push(`mac ${displayValue(ops.mac)}`);
  if (ops.serial && normToken(ops.serial) !== matchedToken) parts.push(`serial ${displayValue(ops.serial)}`);
  if (ops.sim) parts.push(`sim ${trimExact(ops.sim)}`);
  return parts;
};

// Match assets by the asset-code SUFFIX — the segment after the '-' separator.
// The prefix is derived from the asset SUBTYPE and can change after creation, so
// it is not a stable key; the suffix (the asset number) is. We take everything
// after the LAST '-', so "GDC-123" -> "123" and "1E6-123" -> "123" (digits in the
// prefix are ignored). Compared case-insensitively. Returns null when the code
// has no '-' (we can't isolate the suffix, so it can't be matched this way).
export const assetMatchKey = (code) => {
  const s = String(code === null || code === undefined ? '' : code).trim();
  const idx = s.lastIndexOf('-');
  if (idx === -1) return null;
  const suffix = s.slice(idx + 1).trim().toUpperCase();
  return suffix === '' ? null : suffix;
};

// Group a byCode Map by asset match key. Records whose code has no '-' go to
// `noKey` (they can't be suffix-matched and are surfaced as flagged rows).
const indexByAssetKey = (byCode) => {
  const byKey = new Map();
  const noKey = [];
  byCode.forEach((rec) => {
    const k = assetMatchKey(rec.assetCode);
    if (k === null) { noKey.push(rec); return; }
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(rec);
  });
  return { byKey, noKey };
};

/**
 * Compare the two sides and produce one result row per asset plus the per-device
 * detail lines. Returns { rows, summary }.
 *
 * Matching is by shared identifier VALUE and is field/role-agnostic: an AssetPro
 * device matches an Ops device when any of their identifiers are equal (MACs
 * compared colon-insensitively), regardless of which field/role each side stores
 * the id under. Each device is matched at most once (greedy one-to-one).
 */
export function compareSides(apResult, opsResult, options = {}) {
  const apByCode = apResult && apResult.byCode ? apResult.byCode : new Map();
  const opsByCode = opsResult && opsResult.byCode ? opsResult.byCode : new Map();

  // Location resolution (asset-all sends only a location id):
  //   soldLocationIds : Set of location ids whose name is "SOLD" (from /location)
  //   locationsById   : Map location id -> name, for display/export
  const soldLocationIds = options.soldLocationIds instanceof Set ? options.soldLocationIds : null;
  const locationsById = options.locationsById instanceof Map ? options.locationsById : null;

  const rows = [];

  // Compare one AssetPro record against one Ops record (either may be null).
  // `extraReviews` carries join-level concerns (ambiguous / missing asset number)
  // so they surface as needs-review rather than a silent (mis)match.
  const buildRow = (ap, ops, extraReviews) => {
    const apDevices = ap ? ap.devices : [];
    const opsDevices = ops ? ops.devices : [];
    const apCode = ap ? ap.assetCode : null;
    const opsCode = ops ? ops.assetCode : null;
    const code = apCode || opsCode;

    const presence = ap && ops ? 'both' : ap ? 'assetpro_only' : 'ops_only';

    const usedOps = new Set();
    const deviceLines = [];
    const needsReviewReasons = [...(extraReviews || [])];
    let matched = 0;
    let apOnly = 0;
    let opsOnly = 0; // comparable ops-only (real difference)
    let opsInfo = 0; // informational ops-only (e.g. SIM)

    // Precompute each Ops device's comparison tokens once.
    const opsTokenSets = new Map();
    opsDevices.forEach((o) => opsTokenSets.set(o, opsMatchTokens(o)));

    // AssetPro devices -> match against an Ops device that shares the identifier
    // VALUE, regardless of which field/role either side stores it under (a MAC in
    // AssetPro's tagDeviceId still matches an Ops MOVEMENT_SENSOR `mac`). MACs are
    // compared colon/separator-insensitively (see normToken).
    apDevices.forEach((d, i) => {
      const notes = [];
      if (d.fallback) notes.push('AssetPro id from altName');
      if (d.duplicate) notes.push('Duplicate id on AssetPro side');

      const vtok = normToken(d.rawId);
      const opsMatch = vtok
        ? opsDevices.find((o) => !usedOps.has(o) && opsTokenSets.get(o).has(vtok))
        : null;

      if (opsMatch) {
        usedOps.add(opsMatch);
        matched += 1;
        const others = opsOtherIdsExcept(opsMatch, vtok);
        if (others.length) notes.push(`IoT-Ops device also carries ${others.join(', ')}`);
        deviceLines.push({
          key: `ap-${i}`,
          kind: d.kind,
          role: opsMatch.role || null,
          status: 'matched',
          assetProId: displayValue(d.rawId),
          opsId: opsDisplayId(opsMatch),
          notes
        });
      } else {
        apOnly += 1;
        if (d.fallback) {
          // An altName-sourced id that did NOT match is genuinely uncertain — we
          // can't tell a missing-in-Ops device from a wrong altName. Flag it.
          needsReviewReasons.push(`${DEVICE_KIND_LABEL[d.kind]} ${displayValue(d.rawId)}: unmatched id sourced from altName`);
        }
        deviceLines.push({
          key: `ap-${i}`,
          kind: d.kind,
          role: null,
          status: 'only_assetpro',
          assetProId: displayValue(d.rawId),
          opsId: null,
          notes
        });
      }
    });

    // Ops devices with no AssetPro match.
    opsDevices.forEach((o, i) => {
      if (usedOps.has(o)) return;
      const comparable = opsComparable(o);
      const notes = [];
      if (comparable) {
        const primaryToken =
          o.kind === DEVICE_KIND.GPS ? normToken(o.imei)
            : o.kind === DEVICE_KIND.BLE ? normToken(o.mac)
              : o.kind === DEVICE_KIND.FUEL ? normToken(o.serial)
                : null;
        const others = opsOtherIdsExcept(o, primaryToken);
        if (others.length) notes.push(`also carries ${others.join(', ')}`);
        opsOnly += 1;
      } else {
        notes.push('Not tracked as an AssetPro device');
        opsInfo += 1;
      }
      deviceLines.push({
        key: `ops-${i}`,
        kind: o.kind,
        role: o.role || null,
        status: comparable ? 'only_ops' : 'only_ops_info',
        assetProId: null,
        opsId: opsDisplayId(o),
        notes
      });
    });

    // ---- SOLD-location handling ----
    // Sold assets in AssetPro are moved to the reserved "SOLD" location. The
    // conflict is defined by whether IoT-Ops still has an actual DEVICE on the
    // asset — NOT merely whether an asset row exists. IoT-Ops keeps the (empty)
    // installation row after devices are scrapped, so "row exists" is not enough.
    //   - Sold AND IoT-Ops still has a real device on it => conflict (surfaced).
    //   - Sold AND IoT-Ops has no real device (absent row, empty/closed row, or
    //     SIM-only)                                       => expected, hidden.
    // Resolve the location name from the /location list (asset-all only had the
    // id), falling back to any inline name. Sold = the location id is a known
    // SOLD location, or the resolved name is "SOLD".
    const apLocId = ap ? ap.locationId : null;
    const resolvedLocationName = (ap && ap.locationName)
      || (apLocId && locationsById ? locationsById.get(apLocId) || null : null);
    const apSold = ap
      ? Boolean((soldLocationIds && apLocId && soldLocationIds.has(apLocId)) || isSoldLocation(resolvedLocationName))
      : false;
    const opsHasRealDevice = opsDevices.some((o) => opsComparable(o));
    const soldConflict = apSold && opsHasRealDevice;
    const soldHidden = apSold && !opsHasRealDevice;
    if (soldConflict) {
      needsReviewReasons.push('Sold in AssetPro (SOLD location) but IoT-Ops still has a device installed on it.');
    }

    // Asset-level category. Any uncertainty (ambiguous asset number, malformed
    // MAC, unmatched fallback id, sold-but-still-in-Ops) takes precedence and
    // surfaces as needs-review; informational-only Ops devices (SIM) never flip
    // an asset to "mismatch".
    let category;
    if (needsReviewReasons.length > 0) {
      category = CATEGORY.NEEDS_REVIEW;
    } else if (presence === 'assetpro_only') {
      category = CATEGORY.ONLY_ASSETPRO;
    } else if (presence === 'ops_only') {
      category = CATEGORY.ONLY_OPS;
    } else if (apOnly === 0 && opsOnly === 0) {
      category = CATEGORY.MATCH; // comparable devices all line up (incl. zero-device)
    } else {
      category = CATEGORY.DEVICE_MISMATCH;
    }

    return {
      key: apCode || opsCode,
      assetCode: apCode || opsCode,
      // Ops asset code, surfaced separately when the alphabetic prefix has drifted
      // (we joined on the numeric part, so the two codes can legitimately differ).
      opsAssetCode: opsCode,
      assetCodesDiffer: Boolean(apCode && opsCode && apCode !== opsCode),
      presence,
      category,
      // No devices on either side => nothing to reconcile. The report hides these
      // by default (an AssetPro asset with no devices can never match the Ops
      // registry, which only lists installed devices), but the data is kept so a
      // toggle can reveal them and the hidden count stays visible.
      deviceless: apDevices.length === 0 && opsDevices.length === 0,
      // SOLD-location flags (see above). soldHidden rows are hidden by default in
      // the report (expected, not a conflict); soldConflict rows are surfaced.
      apSold,
      soldConflict,
      soldHidden,
      locationName: resolvedLocationName,
      altName: ap ? ap.altName : null,
      ownerCode: ap ? ap.ownerCode : null,
      apDeviceCount: apDevices.length,
      opsDeviceCount: opsDevices.length,
      matched,
      apOnly,
      opsOnly,
      opsInfo,
      needsReviewReasons,
      devices: deviceLines
    };
  };

  // ---- Join on the asset-code suffix (part after '-') ----
  const { byKey: apByKey, noKey: apNoKey } = indexByAssetKey(apByCode);
  const { byKey: opsByKey, noKey: opsNoKey } = indexByAssetKey(opsByCode);
  const keys = new Set([...apByKey.keys(), ...opsByKey.keys()]);

  keys.forEach((k) => {
    const apRecs = apByKey.get(k) || [];
    const opsRecs = opsByKey.get(k) || [];
    if (apRecs.length <= 1 && opsRecs.length <= 1) {
      rows.push(buildRow(apRecs[0] || null, opsRecs[0] || null, []));
    } else {
      // More than one asset shares this suffix on a side — the key is not unique
      // here. Do NOT merge/guess: emit each record unmatched and flag it.
      const apCodes = apRecs.map((r) => r.assetCode);
      const opsCodes = opsRecs.map((r) => r.assetCode);
      const reason = `Ambiguous asset code suffix "${k}": AssetPro [${apCodes.join(', ') || '—'}] vs IoT-Ops [${opsCodes.join(', ') || '—'}] — not auto-matched, resolve manually.`;
      apRecs.forEach((r) => rows.push(buildRow(r, null, [reason])));
      opsRecs.forEach((r) => rows.push(buildRow(null, r, [reason])));
    }
  });

  // Asset codes with no '-' separator can't be suffix-matched — show them, flagged.
  apNoKey.forEach((r) => rows.push(buildRow(r, null, ['AssetPro asset code has no "-" separator — cannot match by suffix.'])));
  opsNoKey.forEach((r) => rows.push(buildRow(null, r, ['IoT-Ops asset code has no "-" separator — cannot match by suffix.'])));

  // Stable sort: problems first (needs review, mismatch, only-*), matches last,
  // then by assetCode.
  const order = {
    [CATEGORY.NEEDS_REVIEW]: 0,
    [CATEGORY.DEVICE_MISMATCH]: 1,
    [CATEGORY.ONLY_ASSETPRO]: 2,
    [CATEGORY.ONLY_OPS]: 3,
    [CATEGORY.MATCH]: 4
  };
  rows.sort((a, b) => {
    const d = (order[a.category] || 0) - (order[b.category] || 0);
    if (d !== 0) return d;
    return String(a.assetCode).localeCompare(String(b.assetCode));
  });

  const summary = {
    totalAssets: rows.length,
    [CATEGORY.MATCH]: 0,
    [CATEGORY.DEVICE_MISMATCH]: 0,
    [CATEGORY.ONLY_ASSETPRO]: 0,
    [CATEGORY.ONLY_OPS]: 0,
    [CATEGORY.NEEDS_REVIEW]: 0
  };
  rows.forEach((r) => { summary[r.category] += 1; });

  return { rows, summary };
}

// Flatten result rows to one record per device line for CSV / xlsx export.
export function toExportRows(rows) {
  const out = [];
  (rows || []).forEach((r) => {
    const opsCode = r.assetCodesDiffer ? (r.opsAssetCode || '') : '';
    if (r.devices.length === 0) {
      out.push({
        assetCode: r.assetCode,
        opsAssetCode: opsCode,
        location: r.locationName || '',
        category: CATEGORY_LABEL[r.category],
        deviceKind: '',
        opsRole: '',
        status: '',
        assetProId: '',
        opsId: '',
        notes: ''
      });
      return;
    }
    r.devices.forEach((d) => {
      out.push({
        assetCode: r.assetCode,
        opsAssetCode: opsCode,
        location: r.locationName || '',
        category: CATEGORY_LABEL[r.category],
        deviceKind: DEVICE_KIND_LABEL[d.kind] || d.kind,
        opsRole: d.role || '',
        status: d.status,
        assetProId: d.assetProId || '',
        opsId: d.opsId || '',
        notes: (d.notes || []).join('; ')
      });
    });
  });
  return out;
}
